import { RouteComponentProps } from "../../../models/RouteComponent";
import { Button } from "primereact/button";
import {
  Groups,
  Person,
  PersonAdd,
  Calendar,
  AssignmentAdd,
  AddToQue,
  Handyman,
  Forklift,
  Colours,
  GroupsAdd,
} from "../../../assets/icons";
import { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { Steps } from "primereact/steps";
import { SiteDetails } from "./Steps/SiteDetails";
import { ClientDetails } from "./Steps/ClientDetails";
import { ClientProfessionalTeam } from "./Steps/ClientProfessionalTeam";
import { Schedule } from "./Steps/Schedule";
import { ScopeOfWork } from "./Steps/ScopeOfWork";
import { capitaliseFirstLetter, snakeToHuman } from "../../../utils/generalUtils";
import { useToast } from "../../../context/ToastContext";
import useApiService from "../../../hooks/useApiService";
import styles from "../Create/add-site.module.css";
import { JobTaskWizard } from "../Create/Steps/JobTasks";
import { EquipmentAndTools } from "../Create/Steps/EquipmentAndTools";
import { Vehicles } from "../Create/Steps/Vehicles";
import { Chemicals } from "../Create/Steps/Chemicals";
import { Employees } from "../Create/Steps/Employees";
import { formDataType } from "../Create/AddSite";
import { Site } from "../../../models/site.interface";

interface DuplicateSiteProps extends RouteComponentProps {
  onCancel?: () => void;
  callback?: (res: any) => void;
  record: Site;
}

function DuplicateSite({ onCancel, callback, record, ...props }: DuplicateSiteProps) {
  const toast = useToast();
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const requiredFieldsWithSteps = [
    { step: 0, fields: ["name", "industry", "address"] },
    { step: 1, fields: [] },
    {
      step: 2,
      fields: [],
    },
    { step: 3, fields: [] },
    { step: 4, fields: ["scope_of_work"] },
  ];
  const [formData, setFormData] = useState<formDataType>({
    name: `${record?.name || ""} Copy`,
    industry: Array.isArray(record?.industry) ? record.industry : [],
    start_at: new Date(),
    end_at: record?.end_at ? new Date(record.end_at) : undefined,
    address: record?.address || "",
    gps_latitude: record?.gps_latitude || null,
    gps_longitude: record?.gps_longitude || null,
    client_name: record?.client_name || "",
    client_agent_name: record?.client_agent_name || "",
    client_agent_phone: record?.client_agent_phone || "",
    client_agent_email: record?.client_agent_email || "",
    client_architect_company_name: record?.client_architect_company_name || "",
    client_architect_name: record?.client_architect_name || "",
    client_architect_phone: record?.client_architect_phone || "",
    client_architect_email: record?.client_architect_email || "",
    client_safety_agent_company_name: record?.client_safety_agent_company_name || "",
    client_safety_agent_name: record?.client_safety_agent_name || "",
    client_safety_agent_phone: record?.client_safety_agent_phone || "",
    client_safety_agent_email: record?.client_safety_agent_email || "",
    client_structural_engineer_company_name: record?.client_structural_engineer_company_name || "",
    client_structural_engineer_name: record?.client_structural_engineer_name || "",
    client_structural_engineer_phone: record?.client_structural_engineer_phone || "",
    client_structural_engineer_email: record?.client_structural_engineer_email || "",
    scope_of_work: record?.scope_of_work || "",
    job_task: Array.isArray(record?.job_task) ? record.job_task : null,
    default_schedule: record?.default_schedule || {
      monday: {
        active: true,
        start: "8:00",
        end: "17:00",
      },
      tuesday: {
        active: true,
        start: "8:00",
        end: "17:00",
      },
      wednesday: {
        active: true,
        start: "8:00",
        end: "17:00",
      },
      thursday: {
        active: true,
        start: "8:00",
        end: "17:00",
      },
      friday: {
        active: true,
        start: "8:00",
        end: "17:00",
      },
      saturday: {
        active: false,
        start: "8:00",
        end: "17:00",
      },
      sunday: {
        active: false,
        start: "8:00",
        end: "17:00",
      },
    },
  });

  const { loading: creatingSite, fetchData: createSite } = useApiService({
    method: "POST",
    url: "/site-duplicate",
    apiName: "core",
    onSuccess: data => {
      toast("success", "Site created successfully", 5000);
      if (callback) callback(data);
    },
    onError: () => {
      toast("error", "Could not create site, please try again later", 5000);
    },
    fetchOnMount: false,
  });

  const { loading: fetchingSite } = useApiService<Site>({
    url: `site-list/${record?.id || ""}`,
    method: "GET",
    apiName: "core",
    onSuccess: data => {
      setFormData(prev => ({
        ...prev,
        equipment: Array.isArray(data?.equipment) ? data.equipment : [],
        vehicle: Array.isArray(data?.vehicle) ? data.vehicle : [],
        employees: Array.isArray(data?.employees) ? data.employees : [],
        chemical: Array.isArray(data?.chemical) ? data.chemical : [],
        job_task_categories: Array.isArray(data?.job_task_categories) ? data.job_task_categories : [],
      }));
    },
    fetchOnMount: !!record?.id,
  });

  useEffect(() => {
    props.setGlobalLoader(fetchingSite);
  }, [props, creatingSite, fetchingSite]);

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    // Clear any existing error for this field
    if (fieldErrors[name]) {
      setFieldErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  }

  function handleNext() {
    // Validate required fields for the current step
    const fieldsToValidate = requiredFieldsWithSteps.find(step => step.step === activeIndex);
    if (fieldsToValidate) {
      const newFieldErrors: { [key: string]: string } = {};
      fieldsToValidate.fields.forEach(field => {
        const fieldValue = formData[field as keyof formDataType];
        const isEmpty = Array.isArray(fieldValue)
          ? fieldValue.length === 0
          : !fieldValue || fieldValue === "";

        if (isEmpty) {
          newFieldErrors[field] = `${capitaliseFirstLetter(snakeToHuman(field))} is required`;
        }
      });
      setFieldErrors(newFieldErrors);
      if (Object.keys(newFieldErrors).length > 0) {
        return; // Prevent moving to the next step if there are validation errors
      }
    }

    if (activeIndex < menuItems.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  }

  async function handleSubmit() {
    // Final submission logic here
    delete formData.job_task_categories;
    await createSite({
      payload: {
        ...formData,
        replication_site: record?.id,
      },
    });
  }

  function handleKeyValueChange(name: string, value: unknown) {
    setFormData({
      ...formData,
      [name]: value,
    });
  }

  const itemRenderer = (
    item: { label: string; icon: ReactNode; command: () => void },
    itemIndex: number
  ) => {
    const isActiveItem = activeIndex === itemIndex;
    const backgroundColor = isActiveItem ? "var(--primary-color)" : "var(--surface-b)";
    const textColor = isActiveItem ? "var(--surface-b)" : "var(--text-color-secondary)";

    return (
      <div
        style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}
        onClick={() => {
          setActiveIndex(itemIndex);
        }}
      >
        <div
          className="inline-flex align-items-center justify-content-center align-items-center border-circle border-primary border-1 h-3rem w-3rem z-1 cursor-pointer"
          style={{ backgroundColor: backgroundColor, color: textColor, marginTop: "-5px" }}
        >
          {item.icon}
        </div>
        <span>{item.label}</span>
      </div>
    );
  };

  const menuItems = [
    {
      label: "Add site",
      icon: <Groups width={28} height={28} />,
      template: (item: any) => itemRenderer(item, 0),
      className: `${styles["step-item"]}`,
    },
    {
      label: "Client details",
      icon: <Person width={28} height={28} />,
      template: (item: any) => itemRenderer(item, 1),
      className: `${styles["step-item"]}`,
    },
    {
      label: "Client professional team",
      icon: <PersonAdd width={28} height={28} />,
      template: (item: any) => itemRenderer(item, 2),
      className: `${styles["step-item"]}`,
    },
    {
      label: "Schedule",
      icon: <Calendar width={28} height={28} />,
      template: (item: any) => itemRenderer(item, 3),
      className: `${styles["step-item"]}`,
    },
    {
      label: "Scope of work",
      icon: <AssignmentAdd width={28} height={28} />,
      template: (item: any) => itemRenderer(item, 4),
      className: `${styles["step-item"]}`,
    },
    {
      label: "Job Tasks",
      icon: <AddToQue width={28} height={28} />,
      template: (item: any) => itemRenderer(item, 5),
      className: `${styles["step-item"]}`,
    },
    {
      label: "Tools & Equipment",
      icon: <Handyman width={28} height={28} />,
      template: (item: any) => itemRenderer(item, 6),
      className: `${styles["step-item"]}`,
    },
    {
      label: "Vehicles",
      icon: <Forklift width={28} height={28} />,
      template: (item: any) => itemRenderer(item, 7),
      className: `${styles["step-item"]}`,
    },
    {
      label: "Chemicals",
      icon: <Colours width={28} height={28} />,
      template: (item: any) => itemRenderer(item, 8),
      className: `${styles["step-item"]}`,
    },
    {
      label: "Employees",
      icon: <GroupsAdd width={28} height={28} />,
      template: (item: any) => itemRenderer(item, 9),
      className: `${styles["step-item"]}`,
    },
  ];
  return (
    <div style={{ height: "100%", position: "relative", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "0 1rem 1rem" }}>
        <Steps model={menuItems} activeIndex={activeIndex} className={`m-2`} readOnly={false} />
        <div>
          {activeIndex === 0 && (
            <SiteDetails
              formData={formData}
              setFormData={setFormData}
              handleInputChange={handleInputChange}
              fieldErrors={fieldErrors}
              setFieldErrors={setFieldErrors}
            />
          )}
          {activeIndex === 1 && (
            <ClientDetails
              formData={formData}
              setFormData={setFormData}
              handleInputChange={handleInputChange}
              fieldErrors={fieldErrors}
            />
          )}
          {activeIndex === 2 && (
            <ClientProfessionalTeam
              handleInputChange={handleInputChange}
              setFormData={setFormData}
              formData={formData}
            />
          )}
          {activeIndex === 3 && <Schedule formData={formData} setFormData={setFormData} />}
          {activeIndex === 4 && <ScopeOfWork formData={formData} setFormData={setFormData} />}
          {activeIndex === 5 && (
            <JobTaskWizard handleChange={handleKeyValueChange} formData={formData} />
          )}
          {activeIndex === 6 && (
            <EquipmentAndTools handleChange={handleKeyValueChange} formData={formData} />
          )}
          {activeIndex === 7 && (
            <Vehicles handleChange={handleKeyValueChange} formData={formData} />
          )}
          {activeIndex === 8 && (
            <Chemicals handleChange={handleKeyValueChange} formData={formData} />
          )}
          {activeIndex === 9 && (
            <Employees handleChange={handleKeyValueChange} formData={formData} />
          )}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          gap: "1rem",
          background: "white",
          paddingTop: "1rem",
          borderTop: "1px solid #e5e7eb",
          flexShrink: 0,
        }}
      >
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          disabled={creatingSite}
          onClick={() => {
            if (onCancel) onCancel();
          }}
        />
        <div>
          {activeIndex !== 0 && (
            <Button
              label="Back"
              color={"primary"}
              outlined
              className="mr-2"
              disabled={creatingSite}
              onClick={() => setActiveIndex(activeIndex - 1)}
            />
          )}
          <Button
            label={activeIndex === menuItems.length - 1 ? "Create Site" : "Next"}
            color={"primary"}
            onClick={async () => {
              if (activeIndex === menuItems.length - 1) {
                // Submit the form
                console.log("Form submitted:", formData);
                await handleSubmit();
              } else {
                handleNext();
              }
            }}
            loading={creatingSite}
            disabled={creatingSite}
          />
        </div>
      </div>
    </div>
  );
}

export default DuplicateSite;
